import { useEffect, useMemo, useRef, useState } from 'react'
import {
  AppProvider,
  optimisticRemoveKey,
  useApp,
  type PendingErstgespraech,
} from './core/state/appStore'
import { removeRow } from './core/lib/optimistic'
import { AuthProvider, useAuth } from './core/state/auth'
import { assertWriteIdentity, captureWriteIdentity, isWriteIdentityCurrent, setActiveOrganisationId } from './core/api/http'
import { bootstrapMe, NeedsOrganisation } from './core/data/bootstrap'
import { me, meAfterBootstrap, currentVariant, setActiveOrganisation, resetMe, variantDriftedFrom } from './core/data/session'
import { CoachingDeleteRefused, deleteCoachingCascade, getCoachingOrganisationId } from './core/data/coachings'
import { resolveAppLink, decideAppLinkOrg } from './core/lib/appLinkResolve'
import { listCoachStatusSicht } from './core/data/coachStatusSicht'
import {
  restrictsToCoachVisibility,
  hiddenStagesFor,
  visibleCoachingFilterStage,
  resolvedHiddenStages,
  boardHiddenGroupsFor,
} from './core/lib/coachVisibility'
import { removeMembership, type Member } from './core/data/members'
import { deleteMassnahme } from './core/data/massnahmen'
import { unlinkModul, deleteModul, isForeignKeyViolation, listModulUsageCounts } from './variants/azav/data/modul'
import { friendlyErrorMessage } from './core/lib/dbError'
import { memberDisplayName, canRemoveMember } from './core/lib/orgMembers'
import { orgSettingsTabsFor } from './core/lib/orgSettingsTabs'
import { Login } from './core/components/auth/Login'
import { OrganisationSetup } from './core/components/auth/OrganisationSetup'
import { ResetPassword } from './core/components/auth/ResetPassword'
import { RegistrationLink } from './core/components/auth/RegistrationLink'
import { isResetPasswordPath, takeResetTokenFromUrl } from './core/components/auth/resetLink'
import { isRegistrationPath, takeRegisteredFlagFromUrl, takeRegistrationTokenFromUrl } from './core/components/auth/registeredLink'
import { AskModal } from './core/components/ui/ask-modal'
import { ZuruecksetzenDialog } from './core/components/dashboard/ZuruecksetzenDialog'
import {
  Button,
  ButtonLink,
  Confirmation,
  Icon,
  Modal,
  ModalHead,
  Pill,
  Toaster,
  toastError,
  toastInfo,
  toastSuccess,
  toastWarning,
} from 'admination-react'
import { Navbar, staffDestinations } from './core/components/Navbar'
import { AccountSignOut, OPEN_ORG_SETTINGS_PARAM } from './core/components/UserMenu'
import { ListView } from './core/views/ListView'
import { BoardView, type BoardHeaderAction, type BoardCardAction, type BoardHeaderFileAction } from './core/views/BoardView'
import { ListToolbar } from './core/views/ListToolbar'
import { useViewTypePref } from './core/hooks/useViewTypePref'
import { useKohortePref } from './core/hooks/useKohortePref'
import { kohortenVon } from './core/lib/kohorte'
import { filterCoachingsByKohorte } from './core/lib/coachingFilter'
import { KohortenSelect } from './core/components/dashboard/KohortenSelect'
import { useAnwesenheitSeite } from './variants/vergabe/components/anwesenheit/useAnwesenheitSeite'
import { useWindowDragGuard } from './core/hooks/useWindowDragGuard'
import { useAmpelSchwellen } from './core/hooks/useAmpelSchwellen'
import { useIncompleteCards } from './core/hooks/useIncompleteCards'
import { useCardMarks } from './core/hooks/useCardMarks'
import {
  descriptionFor,
  immerGelisteteNamen,
  openVertragCoachings,
  sendVertragReminders,
  TeilnehmerakteStaffHost,
  useTeilnehmersicht,
  VERTRAG_REMINDER_ACTION,
  vertragReminderToast,
} from './variants'
import {
  coachingViewConfig,
  coachingGroupOrder,
  coachingGroupLabel,
  zurueckgezogenViewConfig,
} from './core/components/dashboard/coachingViewConfig'
import { coachingBoardConfig, resolveCardOpen } from './core/components/dashboard/coachingBoardConfig'

import { useCoachingFilterRows } from './core/components/dashboard/useCoachingFilterRows'
import { CoachingDocs } from './core/components/dashboard/CoachingDocs'
import { OrgSettings } from './core/components/orgsettings/OrgSettings'
import { AnleitungSection } from './core/components/orgsettings/AnleitungSection'
import { MeineEinstellungen } from './core/components/einstellungen/MeineEinstellungen'
import { MembersSection } from './core/components/orgsettings/MembersSection'
import { MassnahmenSection } from './core/components/orgsettings/MassnahmenSection'
import { ModuleSection } from './variants/azav/components/orgsettings/ModuleSection'
import { memberViewConfig } from './core/components/orgsettings/memberViewConfig'
import { useMemberFilterRows } from './core/components/orgsettings/useMemberFilterRows'
import { massnahmeViewConfig, massnahmeViewConfigMinimal } from './core/components/orgsettings/massnahmeViewConfig'
import { useMassnahmeFilterRows } from './core/components/orgsettings/useMassnahmeFilterRows'
import { moduleViewConfig } from './variants/azav/components/orgsettings/moduleViewConfig'
import { ModalOverlay } from './core/components/modal/ModalOverlay'
import { naturalSortFiles } from './core/lib/files'
import { DropCard } from './core/components/modal/DropCard'
import { ZUWEISUNGSLISTE_ACCEPT, UPLOAD_ACCEPT, isAllowedType, readableUploadTypes } from './core/lib/fileTypes'
import { phaseLandingTab } from './core/hooks/usePhaseNav'
import { docModal } from './core/lib/docToModal'
import { applyCombinedUploadAutoMatch, type AvgsAutoMatchFailure } from './variants/azav/flows/avgs'
import { applyBewilligungUploadAutoMatch, type BewilligungAutoMatchFailure } from './variants/azav/flows/bewilligung'
import { avgsLooksPlausible } from './variants/azav/lib/avgsPlausible'
import { runZuweisungslisteUpload } from './variants/vergabe/components/zuweisungsliste/upload'
import { ZuweisungslisteErgebnisPrompt, ZuweisungslisteFehlerPrompt } from './variants/vergabe/components/zuweisungsliste/prompts'
import { AnlegenWahlPrompt } from './variants/vergabe/components/teilnehmende-anlegen/AnlegenWahlPrompt'
import type { ZuweisungslisteFehlerZeile, ZuweisungslisteResult } from './variants/vergabe/api/zuweisungsliste'
import { anzeigeName, type DashboardCoaching, type Stage } from './core/types'
import type { ModalSource, AppView } from './core/state/appStore'
import { ParticipantApp } from './core/components/teilnehmer/ParticipantApp'
import { AUFGABE as VERGABE_AUFGABE } from './variants/vergabe/aufgabe'
import { isPortalSurface } from './core/lib/appSurface'

const isResetPasswordSurface =
  typeof window !== 'undefined' && isResetPasswordPath(window.location.pathname)
const resetPasswordToken = isResetPasswordSurface ? takeResetTokenFromUrl() : null

const isRegistrationSurface =
  typeof window !== 'undefined' && isRegistrationPath(window.location.pathname)
const registrationToken = isRegistrationSurface ? takeRegistrationTokenFromUrl() : null

const openedFromRegistrationMail =
  typeof window !== 'undefined' && !isResetPasswordSurface && takeRegisteredFlagFromUrl()

function avgsAutoMatchFailureMessage(failure: AvgsAutoMatchFailure): string {
  switch (failure.kind) {
    case 'technical':
      return failure.message
    case 'ambiguous':
      return `Mehrere Teilnehmer passen zu „${failure.vorname} ${failure.nachname}" – bitte das passende Coaching direkt öffnen und die AVGS-Bestätigung dort hochladen.`
    case 'not-ready':
      return `Die Stammdaten von „${failure.vorname} ${failure.nachname}" sind noch nicht vollständig – die AVGS-Bestätigung kann erst danach hochgeladen werden.`
    case 'no-match':
      return failure.vorname && failure.nachname
        ? `Kein Teilnehmer namens „${failure.vorname} ${failure.nachname}" auf AVGS wartend gefunden – bitte das Coaching manuell öffnen.`
        : "Konnte den Teilnehmer nicht in der Datei erkennen – bitte das Coaching manuell öffnen."
    case 'no-candidates':
      return "Aktuell wartet kein Coaching auf eine AVGS-Bestätigung – bitte das Coaching manuell öffnen."
  }
}

function bewilligungAutoMatchFailureMessage(failure: BewilligungAutoMatchFailure): string {
  switch (failure.kind) {
    case 'technical':
      return failure.message
    case 'ambiguous':
      return `Mehrere Teilnehmer passen zu „${failure.nachname}" – bitte das passende Coaching direkt öffnen und die Bewilligung dort hochladen.`
    case 'already-uploaded':
      return `Für „${failure.vorname} ${failure.nachname}" liegt bereits eine Bewilligung vor – bitte das Coaching direkt öffnen, um sie zu ersetzen.`
    case 'no-match':
      return failure.nachname
        ? `Kein Teilnehmer namens „${failure.nachname}" auf Bewilligung wartend gefunden – bitte das Coaching manuell öffnen.`
        : "Konnte den Teilnehmer nicht in der Datei erkennen – bitte das Coaching manuell öffnen."
    case 'no-candidates':
      return "Aktuell wartet kein Coaching auf eine Bewilligung – bitte das Coaching manuell öffnen."
  }
}

type Removal =
  | { kind: 'coaching'; coaching: DashboardCoaching }
  | { kind: 'member'; member: Member }
  | { kind: 'massnahme'; id: string }
  | { kind: 'modul-link'; massnahmeId: string; modulId: string }
  | { kind: 'modul'; id: string }

function removalCopy(target: Removal) {
  switch (target.kind) {
    case 'coaching': return {
      question: "Coaching löschen?",
      description: `Das Coaching von ${anzeigeName(target.coaching)} und ${target.coaching.docCount} Dokument(e) werden endgültig gelöscht. Das kann nicht rückgängig gemacht werden.`,
      confirmLabel: "Endgültig löschen", success: "Coaching gelöscht.",
      log: "Coaching-Löschung fehlgeschlagen:",
    }
    case 'member': return {
      question: "Mitglied entfernen?",
      description: `${memberDisplayName(target.member)} wird aus der Organisation entfernt. Das kann nicht rückgängig gemacht werden.`,
      confirmLabel: "Endgültig entfernen", success: "Mitglied entfernt.",
      log: "Mitglied entfernen fehlgeschlagen:",
    }
    case 'massnahme': return {
      question: "Maßnahme löschen?",
      description: "Die Maßnahme wird endgültig gelöscht. Das kann nicht rückgängig gemacht werden.",
      confirmLabel: "Endgültig löschen", success: "Maßnahme gelöscht.",
      log: "Maßnahme löschen fehlgeschlagen:",
    }
    case 'modul-link': return {
      question: "Modul aus der Maßnahme entfernen?",
      description: "Die Verknüpfung mit dieser Maßnahme wird entfernt. Das Modul bleibt im Katalog erhalten.",
      confirmLabel: "Entfernen", success: "Modul entfernt.",
      log: "Modul entfernen fehlgeschlagen:",
    }
    case 'modul': return {
      question: "Modul löschen?",
      description: "Das Modul wird endgültig aus dem Katalog gelöscht. Ist es noch mit einer Maßnahme verknüpft, wird das Löschen abgelehnt. Das kann nicht rückgängig gemacht werden.",
      confirmLabel: "Endgültig löschen", success: "Modul gelöscht.",
      log: "Modul löschen fehlgeschlagen:",
    }
  }
}

function AppShell({ initialErstgespraech }: { initialErstgespraech?: PendingErstgespraech } = {}) {
  const workspaceIdentity = useRef(captureWriteIdentity()).current
  const {
    state,
    dispatch,
    identity,
    members,
    coachings,
    zurueckgezogen: alleZurueckgezogen,
    coachingsLoading,
    reloadDashboard,
    reloadMembers,
    reloadMassnahmen,
    optimisticDashboard,
    optimisticRemove,
  } = useApp()
  useEffect(() => {
    if (initialErstgespraech) dispatch({ type: 'REOPEN_ERSTGESPRAECH', coachingId: null, pending: initialErstgespraech, source: null })
  }, [dispatch, initialErstgespraech])

  useWindowDragGuard()

  const [coachingsViewType, setCoachingsViewType] = useViewTypePref()
  const [kohorte, setKohorte] = useKohortePref()
  const ampelSchwellen = useAmpelSchwellen()

  const pageView = state.view
  const canDelete = identity?.isOwner ?? null

  const [coachHiddenStages, setCoachHiddenStages] = useState<Stage[] | null>(null)
  const limitedVisibility = identity ? restrictsToCoachVisibility(identity) : null

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (state.screen?.kind) return
      const t = e.target
      if (
        t instanceof HTMLElement &&
        (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)
      )
        return
      if (state.selectedCoachingId) dispatch({ type: 'SET_CONTROL', key: 'selectedCoachingId', value: null })
      if (state.selectedMemberUserId) dispatch({ type: 'SET_CONTROL', key: 'selectedMemberUserId', value: null })
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [state.screen?.kind, state.selectedCoachingId, state.selectedMemberUserId, dispatch])
  useEffect(() => {
    let cancelled = false
    if (limitedVisibility !== null) {
      if (!limitedVisibility) setCoachHiddenStages([])
      else listCoachStatusSicht()
        .then((visibleNames) => { if (!cancelled) setCoachHiddenStages(hiddenStagesFor(visibleNames)) })
        .catch((e) => console.error("listCoachStatusSicht() failed:", e))
    }
    return () => { cancelled = true }
  }, [limitedVisibility])

  useEffect(() => {
    const url = new URL(window.location.href)
    if (url.searchParams.get(OPEN_ORG_SETTINGS_PARAM) !== '1') return
    url.searchParams.delete(OPEN_ORG_SETTINGS_PARAM)
    window.history.replaceState(null, '', url)
    dispatch({ type: 'SET_SETTINGS', settings: 'organisation' })
  }, [dispatch])

  const deepLinkContext = useRef({ workspaceIdentity, dispatch })
  useEffect(() => {
    const { workspaceIdentity, dispatch } = deepLinkContext.current
    if (!window.location.pathname.startsWith('/s/access')) return
    const token = new URLSearchParams(window.location.search).get('token')
    if (!token) return
    let cancelled = false
    const cleanUrl = () => {
      window.history.replaceState(null, '', '/')
    }
    void (async () => {
      const result = await resolveAppLink(token)
      if (cancelled || !isWriteIdentityCurrent(workspaceIdentity)) return
      const meInfo = await me()
      if (cancelled || !isWriteIdentityCurrent(workspaceIdentity)) return
      const orgId = await getCoachingOrganisationId(result.coaching_id)
      if (cancelled || !isWriteIdentityCurrent(workspaceIdentity)) return
      const decision = decideAppLinkOrg(orgId, meInfo)
      if (decision.kind === 'fail') {
        throw new Error(`coaching ${result.coaching_id} has no visible organisation_id`)
      }
      if (decision.kind === 'switch-org') {
        const switched = await setActiveOrganisation(decision.organisationId)
        if (cancelled || !isWriteIdentityCurrent(switched)) return
        window.location.reload()
        return
      }
      dispatch({ type: 'SET_CONTROL', key: 'selectedCoachingId', value: result.coaching_id })
      dispatch({ type: 'OPEN_SCREEN', screen: { kind: 'ecn', tab: 'nachweis', coachingId: result.coaching_id, source: null } })
      cleanUrl()
    })().catch((e) => {
      if (cancelled || !isWriteIdentityCurrent(workspaceIdentity)) return
      console.error("Coach-Deep-Link (ECN) fehlgeschlagen:", e)
      toastError("Link ungültig oder abgelaufen.")
      cleanUrl()
    })
    return () => {
      cancelled = true
    }
  }, [])

  const [removal, setRemoval] = useState<Removal | null>(null)
  const [zuruecksetzenOffen, setZuruecksetzenOffen] = useState(false)
  const isCoachingDeleteLocked = (coaching: DashboardCoaching) =>
    descriptionFor(currentVariant()).isDeleteLocked?.(coaching) ?? false
  const requestCoachingDelete = (coachingId: string) => {
    const coaching = coachings.find((row) => row.id === coachingId)
    if (canDelete && coaching && !isCoachingDeleteLocked(coaching)) setRemoval({ kind: 'coaching', coaching })
  }
  const confirmRemoval = async () => {
    if (!removal) return
    const target = removal
    if (target.kind === 'coaching' && isCoachingDeleteLocked(target.coaching)) {
      setRemoval(null)
      return
    }
    const pending = target.kind === 'coaching' ? optimisticDashboard(removeRow(target.coaching.id))
      : optimisticRemove(target.kind === 'member' ? optimisticRemoveKey('member', target.member.userId)
        : target.kind === 'modul-link' ? optimisticRemoveKey('modul-link', target.massnahmeId, target.modulId)
        : optimisticRemoveKey(target.kind, target.id))
    if (target.kind === 'coaching') {
      if (state.screen?.coachingId === target.coaching.id) dispatch({ type: 'CLOSE' })
      else if (state.selectedCoachingId === target.coaching.id) dispatch({ type: 'SET_CONTROL', key: 'selectedCoachingId', value: null })
    } else if (target.kind === 'member' && state.selectedMemberUserId === target.member.userId) {
      dispatch({ type: 'SET_CONTROL', key: 'selectedMemberUserId', value: null })
    } else if (target.kind === 'massnahme' && state.selectedMassnahmeId === target.id) {
      dispatch({ type: 'SET_CONTROL', key: 'selectedMassnahmeId', value: null })
    } else if (target.kind === 'modul-link' || (target.kind === 'modul' && state.selectedModulId === target.id)) {
      dispatch({ type: 'SET_CONTROL', key: 'selectedModulId', value: null })
    }
    setRemoval(null)
    const copy = removalCopy(target)
    try {
      await (target.kind === 'coaching' ? deleteCoachingCascade(target.coaching.id)
        : target.kind === 'member' ? removeMembership(target.member.userId)
        : target.kind === 'massnahme' ? deleteMassnahme(target.id)
        : target.kind === 'modul-link' ? unlinkModul(target.massnahmeId, target.modulId)
        : deleteModul(target.id))
      if (!isWriteIdentityCurrent(workspaceIdentity)) return
      pending.confirm()
      if (target.kind === 'member') reloadMembers()
      else if (target.kind !== 'coaching') reloadMassnahmen()
      toastInfo(copy.success)
    } catch (error) {
      if (!isWriteIdentityCurrent(workspaceIdentity)) return
      pending.rollback()
      if (target.kind === 'modul' && isForeignKeyViolation(error)) {
        const counts = await listModulUsageCounts().catch(() => new Map<string, number>())
        if (!isWriteIdentityCurrent(workspaceIdentity)) return
        const count = counts.get(target.id) ?? 0
        toastError(count > 0
          ? `Dieses Modul kann nicht gelöscht werden - es wird noch in ${count} Maßnahme${count === 1 ? '' : "n"} verwendet.`
          : "Dieses Modul kann nicht gelöscht werden - es ist noch verknüpft.")
      } else {
        console.error(copy.log, error)
        const fallback = `${target.kind === 'member' || target.kind === 'modul-link' ? "Entfernen" : "Löschen"} fehlgeschlagen. Bitte erneut versuchen.`
        toastError(target.kind === 'coaching'
          ? error instanceof CoachingDeleteRefused ? error.message : fallback
          : friendlyErrorMessage(error, fallback))
      }
    }
  }

  const [pendingReminder, setPendingReminder] = useState<DashboardCoaching[] | null>(null)
  const [reminderBusy, setReminderBusy] = useState(false)
  const confirmReminder = () => {
    if (!pendingReminder || reminderBusy) return
    setReminderBusy(true)
    void sendVertragReminders(pendingReminder)
      .then((result) => {
        if (!isWriteIdentityCurrent(workspaceIdentity)) return
        const toast = vertragReminderToast(result)
        if (toast.tone === 'success') toastSuccess(toast.message)
        else toastError(toast.message)
      })
      .finally(() => {
        if (!isWriteIdentityCurrent(workspaceIdentity)) return
        setReminderBusy(false)
        setPendingReminder(null)
      })
  }

  const [zuweisungslisteBusy, setZuweisungslisteBusy] = useState(false)
  const [zuweisungslistePrompt, setZuweisungslistePrompt] = useState<
    | { kind: 'fehler'; meldung: string; fehler: ZuweisungslisteFehlerZeile[] }
    | { kind: 'ergebnis'; ergebnis: ZuweisungslisteResult }
    | null
  >(null)
  const [anlegenWahl, setAnlegenWahl] = useState(false)

  const [pendingUpload, setPendingUpload] = useState<{ kind: 'avgs' | 'bewilligung'; source: ModalSource } | { kind: 'zuweisungsliste' } | null>(null)
  const [quickUploadBusy, setQuickUploadBusy] = useState(false)

  const startZuweisungslisteUpload = (file: File) => {
    if (zuweisungslisteBusy) return
    return runZuweisungslisteUpload(file, {
      reloadDashboard: () => { setPendingUpload(null); reloadDashboard() },
      onBusyChange: setZuweisungslisteBusy,
      onFehler: (body) => { setPendingUpload(null); setZuweisungslistePrompt({ kind: 'fehler', ...body }) },
      onErgebnis: (ergebnis) => setZuweisungslistePrompt({ kind: 'ergebnis', ergebnis }),
    })
  }

  const openTeilnehmendePersonAnlegen = () => {
    openSeq.current++
    dispatch({
      type: 'OPEN_SCREEN',
      screen: {
        kind: 'teilnehmende-person-anlegen',
        tab: 'form',
        coachingId: null,
        source: { kind: 'bar' },
      },
    })
  }

  const variantDescription = descriptionFor(currentVariant())
  const variantEntry = variantDescription.entry
  const entryAction = variantEntry.action

  const openSeq = useRef(0)
  const allowedViews = [...(canDelete ? variantDescription.orgSettingsTabs : []), ...(variantDescription.anwesenheit ? ['anwesenheit'] : [])]
  const navigate = (view: AppView) => {
    if (view !== 'dashboard' && !allowedViews.includes(view)) return
    if (view !== 'dashboard') (document.activeElement as HTMLElement | null)?.blur()
    openSeq.current++
    dispatch({ type: 'SET_VIEW', view })
  }
  const createRecord = (kind: 'invite-coach' | 'create-massnahme' | 'create-modul') => {
    openSeq.current++
    dispatch({ type: 'OPEN_SCREEN', screen: { kind, tab: 'form', coachingId: null, source: { kind: 'bar' } } })
  }
  const openEntry = () => {
    if (entryAction === 'open-teilnehmende-anlegen') {
      if (!zuweisungslisteBusy) setAnlegenWahl(true)
    } else {
      openSeq.current++
      dispatch({ type: 'OPEN_ERSTGESPRAECH', source: { kind: 'bar' } })
    }
  }

  const onCoachingAction: BoardCardAction<DashboardCoaching> = (action, coaching) => {
    dispatch({ type: 'SET_CONTROL', key: 'selectedCoachingId', value: coaching.id })
    dispatch({ type: 'SELECT_COACHING_DOC', name: null })
    if (action === 'open-teilnehmerakte' && coaching.aufgabe !== VERGABE_AUFGABE.potenzialanalyseAusfuellen.dbName) {
      dispatch({ type: 'OPEN_PARTICIPANT', screen: { kind: 'akte', participant: { id: coaching.id, name: anzeigeName(coaching) }, source: { kind: 'column' } } })
      return
    }
    const open = resolveCardOpen(action, coaching.aufgabe)
    if (!open) return
    const source = {
      kind: 'column' as const,
    }
    openSeq.current++
    dispatch({ type: 'OPEN_SCREEN', screen: { kind: open.modal, tab: open.tab, coachingId: coaching.id, source } })
  }

  const onCoachingRowClick = (coaching: DashboardCoaching) => {
    const source = { kind: 'row' as const }
    openSeq.current++
    dispatch({ type: 'OPEN_SCREEN', screen: { kind: 'personal', tab: 'details', coachingId: coaching.id, source, document: { name: null, id: null } } })
  }

  const onCoachingDocClick = (
    coachingId: string,
    docName: string,
    sheet: boolean,
  ) => {
    const modal = alleZurueckgezogen.some((c) => c.id === coachingId) ? 'dokument' : docModal(docName, sheet)
    dispatch({ type: 'SET_CONTROL', key: 'selectedCoachingId', value: coachingId })
    dispatch({ type: 'SELECT_COACHING_DOC', name: docName })
    const source = { kind: 'row' as const }
    const seq = ++openSeq.current
    void phaseLandingTab(modal, coachingId).then((tab) => {
      if (openSeq.current !== seq || !isWriteIdentityCurrent(workspaceIdentity)) return
      dispatch({ type: 'OPEN_SCREEN', screen: { kind: modal, tab, coachingId, source, document: { name: docName, id: null } } })
    })
  }

  const onMemberRowClick = (member: Member) => {
    const source = { kind: 'row' as const }
    openSeq.current++
    dispatch({ type: 'OPEN_SCREEN', screen: { kind: 'member', memberId: member.userId, tab: 'mitglied', coachingId: null, source } })
  }

  const runAvgsQuickAddWithFiles = async (files: File[], source: ModalSource): Promise<void> => {
    const seq = ++openSeq.current
    try {
      const result = await applyCombinedUploadAutoMatch(files)
      if (!isWriteIdentityCurrent(workspaceIdentity)) return
      if (!result.ok) {
        toastError(avgsAutoMatchFailureMessage(result))
        return
      }
      reloadDashboard()
      if (openSeq.current !== seq || !isWriteIdentityCurrent(workspaceIdentity)) return
      if (result.warnings?.length) {
        for (const w of result.warnings) toastWarning(w)
      } else if (!result.tbStored) {
        toastWarning("Nur eine Seite erkannt – als AVGS gespeichert. Die Trägerbestätigung fehlt noch.")
      } else if (result.form && !avgsLooksPlausible(result.form)) {
        toastWarning(
          "Das AVGS-Dokument sieht nicht wie ein Gutschein aus – bitte im Korrekturformular prüfen.",
        )
      }
      setPendingUpload(null)
      dispatch({ type: 'OPEN_SCREEN', screen: { kind: 'avgs', tab: 'avgs', coachingId: result.coachingId, source } })
    } catch (e) {
      if (!isWriteIdentityCurrent(workspaceIdentity)) return
      console.error("AVGS/Trägerbestätigung-Upload fehlgeschlagen:", e)
      toastError("AVGS/Trägerbestätigung-Upload fehlgeschlagen. Bitte erneut versuchen.")
    }
  }

  const runBewilligungQuickAddWithFile = async (file: File, source: ModalSource): Promise<void> => {
    const seq = ++openSeq.current
    try {
      const result = await applyBewilligungUploadAutoMatch(file)
      if (!isWriteIdentityCurrent(workspaceIdentity)) return
      if (!result.ok) {
        toastError(bewilligungAutoMatchFailureMessage(result))
        return
      }
      reloadDashboard()
      if (openSeq.current !== seq || !isWriteIdentityCurrent(workspaceIdentity)) return
      setPendingUpload(null)
      dispatch({ type: 'OPEN_SCREEN', screen: { kind: 'bewilligung', tab: 'bewilligung', coachingId: result.coachingId, source } })
    } catch (e) {
      if (!isWriteIdentityCurrent(workspaceIdentity)) return
      console.error("Bewilligung-Upload fehlgeschlagen:", e)
      toastError("Bewilligung-Upload fehlgeschlagen. Bitte erneut versuchen.")
    }
  }

  const onHeaderFile: BoardHeaderFileAction = (action, files) => {
    if (files.length === 0) return
    if (action === 'upload-avgs') {
      if (!files.every((f) => isAllowedType(f, UPLOAD_ACCEPT))) {
        toastError(`Dateiformat nicht unterstützt – bitte ${readableUploadTypes(UPLOAD_ACCEPT)} hochladen.`)
        return
      }
      const source: ModalSource = { kind: 'column' }
      return runAvgsQuickAddWithFiles(naturalSortFiles([...files]), source)
    }
    if (action === 'upload-bewilligung') {
      if (files.length > 1) {
        toastError("Eine Bewilligung besteht aus genau einer Datei – bitte nur eine Datei hochladen.")
        return
      }
      const file = files[0]
      if (!isAllowedType(file, UPLOAD_ACCEPT)) {
        toastError(`Dateiformat nicht unterstützt – bitte ${readableUploadTypes(UPLOAD_ACCEPT)} hochladen.`)
        return
      }
      const source: ModalSource = { kind: 'column' }
      return runBewilligungQuickAddWithFile(file, source)
    }
  }

  const onHeaderAction: BoardHeaderAction<DashboardCoaching> = (action, _event, rows) => {
    if (action === VERTRAG_REMINDER_ACTION) {
      const targets = openVertragCoachings(rows)
      if (targets.length > 0) setPendingReminder(targets)
      return
    }
    if (action === 'open-ecn') {
      const coaching = coachings.find((c) => c.id === state.selectedCoachingId && c.stage === 'nachweis')
      if (!coaching) return
      const source = {
        kind: 'column' as const,
      }
      openSeq.current++
      dispatch({ type: 'OPEN_SCREEN', screen: { kind: 'ecn', tab: 'nachweis', coachingId: coaching.id, source } })
      return
    }

  }

  const onQuickAdd = (action: string) => {
    if (action === 'upload-avgs') setPendingUpload({ kind: 'avgs', source: { kind: 'bar' } })
    else if (action === 'upload-bewilligung') setPendingUpload({ kind: 'bewilligung', source: { kind: 'bar' } })
  }

  const filterCol =
    pageView === 'dashboard' && coachingsViewType === 'list' && state.coachingFilter
      ? descriptionFor(currentVariant()).columns.find((c) => c.id === state.coachingFilter)
      : undefined
  const quickAddBolt = filterCol?.headerAction ? (
    <div className="pill">
      <Button
        type="button"
        iconOnly
        aria-label={`Schnell hinzufügen: ${filterCol.dbName}`}
        title={`Schnell hinzufügen: ${filterCol.dbName}`}
        onClick={() => onQuickAdd(filterCol.headerAction!)}
      >
        <Icon name="i-bolt" />
      </Button>
    </div>
  ) : undefined

  const inOrgSettings = state.settings === 'organisation'
  const inEinstellungen = state.settings === 'personal'
  const inSettingsView = inOrgSettings || inEinstellungen
  const settingsTabs = orgSettingsTabsFor(variantDescription)
  const orgSettingsTab = state.orgSettingsTab
  const inCoachesView = pageView === 'coaches'
  const inMassnahmenView = pageView === 'massnahmen'
  const inModuleView = pageView === 'module'
  const inAnleitungView = inOrgSettings && orgSettingsTab === 'anleitung'
  const nonCoachingView = pageView !== 'dashboard'
  const selectedMember = members.find((m) => m.userId === state.selectedMemberUserId)

  useEffect(() => {
    if (!state.settings && ['coaches', 'massnahmen', 'module'].includes(state.view) && canDelete !== null && (!canDelete || !variantDescription.orgSettingsTabs.includes(state.view))) {
      dispatch({ type: 'SET_VIEW', view: 'dashboard' })
    }
    if (state.view === 'anwesenheit' && !variantDescription.anwesenheit) dispatch({ type: 'SET_VIEW', view: 'dashboard' })
  }, [state.view, state.settings, canDelete, variantDescription, dispatch])

  const anwesenheitSeite = useAnwesenheitSeite(pageView === 'anwesenheit', kohorte, setKohorte)

  const effectiveHiddenStages = resolvedHiddenStages(coachHiddenStages)

  const boardHiddenGroups = useMemo(
    () => boardHiddenGroupsFor(state.dashboardHiddenStages, coachHiddenStages),
    [state.dashboardHiddenStages, coachHiddenStages],
  )
  const incompleteCardIds = useIncompleteCards(
    coachings,
    state.dashboardVersion,
    pageView === 'dashboard' && coachingsViewType === 'board',
    boardHiddenGroups,
  )
  const cardMarks = useCardMarks()
  const coachingGroupOrderVisible = useMemo(
    () => coachingGroupOrder().filter((id) => !effectiveHiddenStages.includes(id)),
    [effectiveHiddenStages],
  )
  const zurueckgezogen = useMemo(
    () => alleZurueckgezogen.filter((c) => !effectiveHiddenStages.includes(c.stage)),
    [alleZurueckgezogen, effectiveHiddenStages],
  )

  const effectiveCoachingFilter = visibleCoachingFilterStage(state.coachingFilter, effectiveHiddenStages)
  useEffect(() => {
    if (state.coachingFilter !== effectiveCoachingFilter) {
      dispatch({ type: 'SET_CONTROL', key: 'coachingFilter', value: effectiveCoachingFilter })
    }
  }, [state.coachingFilter, effectiveCoachingFilter, dispatch])

  const fullBoardItems = useMemo(() => [...coachings, ...alleZurueckgezogen], [coachings, alleZurueckgezogen])
  const boardItems = useMemo(() => [...coachings, ...zurueckgezogen], [coachings, zurueckgezogen])
  const kohorten = useMemo(() => kohortenVon(boardItems), [boardItems])
  const effectiveKohorte = kohorten.some((k) => k.beginn === kohorte) ? kohorte : null
  const coachingViewState = useMemo(
    () => ({
      sort: state.coachingSort,
      search: state.coachingSearch,
      filter: { ...state.coachingViewFilter, kohorte: effectiveKohorte },
    }),
    [state.coachingSort, state.coachingSearch, state.coachingViewFilter, effectiveKohorte],
  )
  const kohorteFilter = useMemo(() => ({ kohorte: effectiveKohorte }), [effectiveKohorte])
  const coachingsInKohorte = useMemo(() => filterCoachingsByKohorte(coachings, kohorteFilter), [coachings, kohorteFilter])
  const zurueckgezogenInKohorte = useMemo(
    () => filterCoachingsByKohorte(zurueckgezogen, kohorteFilter),
    [zurueckgezogen, kohorteFilter],
  )

  const coachingFilterRows = useCoachingFilterRows(
    coachingsInKohorte,
    coachingGroupOrderVisible,
    coachingsViewType,
    zurueckgezogenInKohorte,
    fullBoardItems,
  )
  const memberFilterRows = useMemberFilterRows()
  const massnahmeFilterRows = useMassnahmeFilterRows()
  const moduleExcludedUsage = state.moduleViewFilter.usage
  const moduleFilterRows = moduleViewConfig.filterCategories!.map((category) => ({
    kind: 'options' as const, id: category.id, label: category.label, options: category.catalog([]),
    excluded: moduleExcludedUsage, count: moduleExcludedUsage.length,
    onToggle: (key: string) => dispatch({ type: 'SET_CONTROL', key: 'moduleViewFilter', value: { usage: moduleExcludedUsage.includes(key) ? moduleExcludedUsage.filter((value) => value !== key) : [...moduleExcludedUsage, key] } }),
  }))

  const massnahmeListConfig = variantDescription.hasModules ? massnahmeViewConfig : massnahmeViewConfigMinimal

  const closeSettings = () => {
    ;(document.activeElement as HTMLElement | null)?.blur()
    dispatch({ type: 'SET_VIEW', view: state.view })
  }
  const settingsContent = inEinstellungen ? <MeineEinstellungen onSaved={reloadMembers} />
    : inAnleitungView && variantDescription.anleitung ? <AnleitungSection url={variantDescription.anleitung} />
    : <OrgSettings />
  const coachingTools = (
    <>
      <ListToolbar
        config={coachingViewConfig}
        viewType={coachingsViewType}
        onViewTypeChange={(view) => {
          if (view === coachingsViewType) return
          openSeq.current++
          ;(document.activeElement as HTMLElement | null)?.blur()
          dispatch({ type: 'CLOSE' })
          setCoachingsViewType(view)
        }}
        filterRows={coachingFilterRows.rows}
        filterActive={coachingFilterRows.active}
        sort={state.coachingSort}
        onSortChange={(sort) => dispatch({ type: 'SET_CONTROL', key: 'coachingSort', value: sort })}
        search={state.coachingSearch}
        onSearchChange={(query) => dispatch({ type: 'SET_CONTROL', key: 'coachingSearch', value: query })}
      />
      {variantDescription.kohorten && (
        <KohortenSelect kohorten={kohorten} value={effectiveKohorte} onChange={setKohorte} />
      )}
    </>
  )
  const navigation = (
    <Navbar
      view={pageView} allowedViews={allowedViews} onNavigate={navigate}
      onReset={!nonCoachingView && canDelete && variantDescription.zuruecksetzen ? () => setZuruecksetzenOffen(true) : undefined}
      onTeilnehmersicht={!nonCoachingView && variantDescription.anwesenheit ? variantDescription.anwesenheit.starten : undefined}
      onEntry={!nonCoachingView ? openEntry : undefined}
      onInvite={canDelete && inCoachesView ? () => createRecord('invite-coach') : undefined}
      onCreateMassnahme={canDelete && inMassnahmenView ? () => createRecord('create-massnahme') : undefined}
      onCreateModul={canDelete && inModuleView ? () => createRecord('create-modul') : undefined}
      entryActive={state.screen?.kind === 'erstgespraech'}
      onOpenSettings={() => dispatch({ type: 'SET_SETTINGS', settings: 'organisation' })}
      onOpenMeineEinstellungen={() => dispatch({ type: 'SET_SETTINGS', settings: 'personal' })}
      tools={
        inCoachesView ? (
          <ListToolbar
            config={memberViewConfig}
            filterRows={memberFilterRows.rows}
            filterActive={memberFilterRows.active}
            sort={state.memberSort}
            onSortChange={(sort) => dispatch({ type: 'SET_CONTROL', key: 'memberSort', value: sort })}
            search={state.memberSearch}
            onSearchChange={(query) => dispatch({ type: 'SET_CONTROL', key: 'memberSearch', value: query })}
          />
        ) : pageView === 'anwesenheit' ? (
          anwesenheitSeite.tools
        ) : pageView === 'dashboard' ? (
          coachingTools
        ) : inMassnahmenView ? (
          <ListToolbar
            config={massnahmeListConfig}
            filterRows={massnahmeFilterRows.rows}
            filterActive={massnahmeFilterRows.active}
            sort={state.massnahmeSort}
            onSortChange={(sort) => dispatch({ type: 'SET_CONTROL', key: 'massnahmeSort', value: sort })}
            search={state.massnahmeSearch}
            onSearchChange={(query) => dispatch({ type: 'SET_CONTROL', key: 'massnahmeSearch', value: query })}
          />
        ) : inModuleView ? (
          <ListToolbar
            config={moduleViewConfig}
            filterRows={moduleFilterRows}
            filterActive={moduleExcludedUsage.length > 0}
            sort={state.moduleSort}
            onSortChange={(sort) => dispatch({ type: 'SET_CONTROL', key: 'moduleSort', value: sort })}
            search={state.moduleSearch}
            onSearchChange={(query) => dispatch({ type: 'SET_CONTROL', key: 'moduleSearch', value: query })}
          />
        ) : null
      }
      rightExtra={pageView === 'dashboard' ? quickAddBolt : undefined}
      entry={variantEntry}
      entryBusy={zuweisungslisteBusy}
    />
  )

  const settingsNavigation = <Navbar onClose={closeSettings} personalSettings={inEinstellungen} inert={state.workspaceSwitching}
    onAnleitung={inAnleitungView && variantDescription.anleitung ? () => window.open(variantDescription.anleitung!, '_blank', 'noopener') : undefined}
    orgSettingsTab={inOrgSettings ? orgSettingsTab : undefined} orgSettingsTabIds={settingsTabs}
    onOrgSettingsTabChange={(tab) => dispatch({ type: 'SET_CONTROL', key: 'orgSettingsTab', value: tab })} />

  const currentCoaching = coachings.find((coaching) => coaching.id === state.screen?.coachingId)
  const deleteCurrentRecord = !canDelete ? undefined
    : state.screen?.kind === 'personal' && state.screen?.tab === 'uebersicht' && currentCoaching && !isCoachingDeleteLocked(currentCoaching) ? () => requestCoachingDelete(currentCoaching.id)
    : state.screen?.kind === 'member' && selectedMember && canRemoveMember(members, selectedMember.userId) ? () => setRemoval({ kind: 'member', member: selectedMember })
    : state.screen?.kind === 'massnahme' && state.selectedMassnahmeId ? () => setRemoval({ kind: 'massnahme', id: state.selectedMassnahmeId! })
    : state.screen?.kind === 'modul' && state.selectedModulId ? () => {
      if (state.selectedMassnahmeId) setRemoval({ kind: 'modul-link', massnahmeId: state.selectedMassnahmeId, modulId: state.selectedModulId! })
      else setRemoval({ kind: 'modul', id: state.selectedModulId! })
    } : undefined
  const recordActions = deleteCurrentRecord ? <Pill><Button variant="danger" onClick={deleteCurrentRecord}><Icon name="i-trash" />Löschen</Button></Pill> : undefined

  const removalDetails = removal && removalCopy(removal)
  const deleteConfirmation = removalDetails && <AskModal open icon="i-trash" iconStroke variant="is-error"
    question={removalDetails.question} description={removalDetails.description}
    confirmLabel={removalDetails.confirmLabel} cancelLabel="Abbrechen"
    onConfirm={() => void confirmRemoval()} onCancel={() => setRemoval(null)} />

  return (
    <>
      <TeilnehmerakteStaffHost />
      <div
        className="page"
        id="page"
        onClick={(e) => {
          if (
            state.screen?.kind ||
            (!state.selectedCoachingId && !state.selectedMemberUserId && !state.selectedMassnahmeId)
          )
            return
          const t = e.target
          if (
            t instanceof Element &&
            t.closest(
              '.card, .list-row, .list-group-header, .bar, .pill, button, input, textarea, .page-overlay',
            )
          )
            return
          if (state.selectedCoachingId) dispatch({ type: 'SET_CONTROL', key: 'selectedCoachingId', value: null })
          if (state.selectedMemberUserId) dispatch({ type: 'SET_CONTROL', key: 'selectedMemberUserId', value: null })
          if (state.selectedMassnahmeId) dispatch({ type: 'SET_CONTROL', key: 'selectedMassnahmeId', value: null })
        }}
      >
        <div className="layout" id="base-layout" data-main-layout>
          <h1 className="visually-hidden" tabIndex={-1}>
            {staffDestinations.find((item) => item.view === pageView)?.text ?? "Coachings"}
          </h1>
          {navigation}
          {inCoachesView ? <MembersSection members={members} coachings={coachings} onRowClick={onMemberRowClick} />
            : inMassnahmenView ? <MassnahmenSection />
            : inModuleView && variantDescription.hasModules ? <ModuleSection />
            : pageView === 'anwesenheit' ? anwesenheitSeite.body
            : coachingsViewType === 'list' ? (
            <ListView
              {...(effectiveCoachingFilter === 'zurueckgezogen'
                ? { items: zurueckgezogen, config: zurueckgezogenViewConfig }
                : { items: coachings, config: coachingViewConfig })}
              viewState={coachingViewState}
              selectedKey={state.screen?.coachingId ?? state.selectedCoachingId}
              onRowClick={onCoachingRowClick}
              renderExpansion={(row) => (
                <CoachingDocs
                  coachingId={row.id}
                  documents={row.documents}
                  gelistet={immerGelisteteNamen(variantDescription.gelisteteDokumente)}
                  sheets={row.sheets}
                  sitzungDaten={row.sitzungDaten}
                  onDocClick={onCoachingDocClick}
                />
              )}
              {...(effectiveCoachingFilter === 'zurueckgezogen'
                ? {}
                : effectiveCoachingFilter
                ? { flatFilter: (row: DashboardCoaching) => row.stage === effectiveCoachingFilter }
                : {
                    groupOrder: coachingGroupOrderVisible,
                    groupHeader: (id: string) => coachingGroupLabel(id),
                    groupTrailing: (_id: string, count: number) => (
                      <span className="list-group-count">{count}</span>
                    ),
                  })}
            />
          ) : (
            <BoardView
              items={boardItems}
              loading={coachingsLoading}
              config={coachingBoardConfig(ampelSchwellen, new Date(), incompleteCardIds, cardMarks)}
              viewState={coachingViewState}
              activeKey={state.screen?.coachingId ?? state.selectedCoachingId}
              hiddenGroups={boardHiddenGroups}
              onCardAction={onCoachingAction}
              onHeaderAction={onHeaderAction}
              onHeaderFile={onHeaderFile}
            />
          )}
        </div>
        <ModalOverlay recordActions={recordActions}>{removal?.kind === 'coaching' ? deleteConfirmation : null}</ModalOverlay>
        {inSettingsView && <Modal label={inEinstellungen ? "Meine Einstellungen" : "Organisationseinstellungen"} onClose={closeSettings} busy={state.workspaceSwitching}>
          {settingsNavigation}
          {settingsContent}
        </Modal>}
      </div>
      {zuruecksetzenOffen && variantDescription.zuruecksetzen && (
        <ZuruecksetzenDialog
          coachings={coachings}
          spalten={variantDescription.columns}
          vorgewaehlt={state.selectedCoachingId ? [state.selectedCoachingId] : []}
          zuruecksetzen={variantDescription.zuruecksetzen}
          onDone={() => {
            if (!isWriteIdentityCurrent(workspaceIdentity)) return
            setZuruecksetzenOffen(false)
            reloadDashboard()
            toastSuccess("Die Auswahl wurde zurückgesetzt.")
          }}
          onCancel={() => setZuruecksetzenOffen(false)}
        />
      )}
      {(!state.screen?.kind || removal?.kind !== 'coaching') && deleteConfirmation}
      <AskModal
        open={pendingReminder !== null}
        icon="i-send"
        iconStroke
        variant="is-info"
        question="Erinnerung senden?"
        description={`Die Erinnerung zur Vertragsunterschrift geht per E-Mail an ${pendingReminder?.length ?? 0} Teilnehmende mit offenem Vertrag.`}
        confirmLabel="Erinnerung senden"
        cancelLabel="Abbrechen"
        busy={reminderBusy}
        onConfirm={confirmReminder}
        onCancel={() => setPendingReminder(null)}
      />
      {anlegenWahl && (
        <AnlegenWahlPrompt
          onPerson={() => {
            setAnlegenWahl(false)
            openTeilnehmendePersonAnlegen()
          }}
          onListe={() => {
            setAnlegenWahl(false)
            setPendingUpload({ kind: 'zuweisungsliste' })
          }}
          onClose={() => setAnlegenWahl(false)}
        />
      )}
      {pendingUpload && <Modal size="document" label={pendingUpload.kind === 'zuweisungsliste' ? "Zuweisungsliste hochladen" : pendingUpload.kind === 'avgs' ? "AVGS hochladen" : "Bewilligung hochladen"}
        busy={quickUploadBusy || zuweisungslisteBusy} onClose={() => setPendingUpload(null)}
        topbar={<ModalHead title={pendingUpload.kind === 'zuweisungsliste' ? "Zuweisungsliste" : pendingUpload.kind === 'avgs' ? "AVGS" : "Bewilligung"}
          closeLabel="Schließen" onClose={() => setPendingUpload(null)} closeDisabled={quickUploadBusy || zuweisungslisteBusy} />}>
        <DropCard title={pendingUpload.kind === 'zuweisungsliste' ? "Zuweisungsliste als Excel-Datei auswählen oder hierher ziehen" : pendingUpload.kind === 'avgs' ? "Nur AVGS oder AVGS und Trägerbestätigung hochladen." : "Bewilligung auswählen oder hierher ziehen"}
          body={pendingUpload.kind === 'zuweisungsliste' ? "Laden Sie die Zuweisungsliste als XLSX-Datei hoch." : "PDF, JPG oder PNG"}
          accept={pendingUpload.kind === 'zuweisungsliste' ? ZUWEISUNGSLISTE_ACCEPT : UPLOAD_ACCEPT}
          multiple={pendingUpload.kind === 'avgs'} loading={quickUploadBusy || zuweisungslisteBusy} loadingLabel="Dokumente werden hochgeladen …"
          onFiles={(files) => {
            if (pendingUpload.kind !== 'avgs' || quickUploadBusy) return
            setQuickUploadBusy(true)
            void runAvgsQuickAddWithFiles(files, pendingUpload.source).finally(() => { if (isWriteIdentityCurrent(workspaceIdentity)) setQuickUploadBusy(false) })
          }}
          onFile={(file) => {
            if (quickUploadBusy) return
            setQuickUploadBusy(true)
            const upload = pendingUpload.kind === 'zuweisungsliste' ? startZuweisungslisteUpload(file) : runBewilligungQuickAddWithFile(file, pendingUpload.source)
            void Promise.resolve(upload).finally(() => { if (isWriteIdentityCurrent(workspaceIdentity)) setQuickUploadBusy(false) })
          }} />
      </Modal>}
      {zuweisungslistePrompt?.kind === 'fehler' && (
        <ZuweisungslisteFehlerPrompt
          meldung={zuweisungslistePrompt.meldung}
          fehler={zuweisungslistePrompt.fehler}
          onClose={() => setZuweisungslistePrompt(null)}
        />
      )}
      {zuweisungslistePrompt?.kind === 'ergebnis' && (
        <ZuweisungslisteErgebnisPrompt
          ergebnis={zuweisungslistePrompt.ergebnis}
          onClose={() => setZuweisungslistePrompt(null)}
        />
      )}
    </>
  )
}

export function Splash({ label }: { label: string }) {
  return (
    <div className="page" id="page">
      <div
        className="layout layout--status"
        id="base-layout"
      >
        {label}
      </div>
    </div>
  )
}

type WorkspaceState = {
  userId: string
  status: 'ready' | 'needs-organisation' | 'failed'
}

function SessionRecovery({ participant = false, onRetry }: { participant?: boolean; onRetry?: () => void }) {
  return <div className="page"><div className="layout layout--center" id="base-layout">
    <div className="confirmation-panel">
      <Confirmation inline tone={participant ? 'info' : 'error'} title={participant ? "Sie sind als teilnehmende Person angemeldet" : "Arbeitsbereich konnte nicht geladen werden"}>
        {participant ? "Öffnen Sie Ihr Teilnehmerportal oder melden Sie sich ab, um sich als Mitarbeiter anzumelden." : "Versuchen Sie es erneut. Sie können sich auch abmelden und anschließend wieder anmelden."}
      </Confirmation>
      <div className="confirmation-actions">
        {participant && <ButtonLink href="/t" variant="primary"><Icon name="i-person" />Teilnehmerportal öffnen</ButtonLink>}
        {onRetry && <Button variant="primary" onClick={onRetry}>Erneut versuchen</Button>}
        <AccountSignOut />
      </div>
    </div>
  </div></div>
}

function Gate() {
  const { session, loading, refresh } = useAuth()
  const [workspace, setWorkspace] = useState<WorkspaceState | null>(null)
  const [initialErstgespraech, setInitialErstgespraech] = useState<{ userId: string; draft: PendingErstgespraech }>()
  const [attempt, setAttempt] = useState(0)
  const teilnehmersicht = useTeilnehmersicht()
  const userId = session?.user_id
  const adminWithoutOrganisation = !!session?.ist_admin && session.organisations.length === 0
  const status = workspace && workspace.userId === userId ? workspace.status : 'loading'
  const sessionRef = useRef(session)
  sessionRef.current = session

  useEffect(() => {
    const session = sessionRef.current
    setActiveOrganisationId(null)
    resetMe()
    setWorkspace(null)
    if (!session || session.is_participant || adminWithoutOrganisation) return
    let cancelled = false
    bootstrapMe(session)
      .then((boot) => {
        if (!cancelled) return meAfterBootstrap(session, boot)
      })
      .then(() => {
        if (!cancelled) setWorkspace({ userId: session.user_id, status: 'ready' })
      })
      .catch((e) => {
        if (cancelled) return
        if (e instanceof NeedsOrganisation) {
          setWorkspace({ userId: session.user_id, status: 'needs-organisation' })
        } else {
          console.error("bootstrap failed:", e)
          setWorkspace({ userId: session.user_id, status: 'failed' })
        }
      })
    return () => {
      cancelled = true
    }
  }, [userId, session?.is_participant, adminWithoutOrganisation, attempt])

  useEffect(() => {
    if (status === 'ready' && session && variantDriftedFrom(session)) window.location.reload()
  }, [session, status])

  async function handleOrganisationCreated(draft?: PendingErstgespraech) {
    resetMe()
    const identity = captureWriteIdentity()
    await refresh()
    assertWriteIdentity(identity)
    setInitialErstgespraech(draft && session ? { userId: session.user_id, draft } : undefined)
    setWorkspace(null)
    setAttempt((n) => n + 1)
  }

  if (loading) return <Splash label="Ihre Sitzung wird geladen …" />
  if (!session) return <Login registered={openedFromRegistrationMail} />
  if (session.is_participant) return <SessionRecovery participant />
  if (adminWithoutOrganisation || status === 'needs-organisation') {
    return <OrganisationSetup key={userId} session={session} onCreated={handleOrganisationCreated} />
  }
  if (status === 'loading') return <Splash label="Ihr Arbeitsbereich wird eingerichtet …" />
  if (status === 'failed') {
    return <SessionRecovery onRetry={() => { void refresh().then(() => setAttempt((n) => n + 1)).catch((error: unknown) => toastError(friendlyErrorMessage(error, "Die Sitzung konnte nicht geladen werden. Bitte melden Sie sich erneut an."))) }} />
  }
  const anwesenheit = descriptionFor(currentVariant()).anwesenheit
  if (teilnehmersicht.aktiv && anwesenheit) return <anwesenheit.Teilnehmersicht />
  return (
    <AppProvider>
      <AppShell initialErstgespraech={initialErstgespraech?.userId === session.user_id ? initialErstgespraech.draft : undefined} />
    </AppProvider>
  )
}

function StaffGate() {
  const { session } = useAuth()
  return <Toaster key={session?.user_id}><Gate /></Toaster>
}

export function App() {
  if (isPortalSurface) {
    return <ParticipantApp />
  }
  if (isResetPasswordSurface) {
    return <ResetPassword token={resetPasswordToken} />
  }
  return (
    <AuthProvider>
      {isRegistrationSurface
        ? <RegistrationLink token={registrationToken}><StaffGate /></RegistrationLink>
        : <StaffGate />}
    </AuthProvider>
  )
}
